import { Information } from "@/api/information";
import ConfirmModal from "@/components/ConfirmModal";
import { MESSAGE_NO_E39 } from "@/config";
import { useLoadingStore } from "@/stores/loading";
import moment from "moment";
import { FC, useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SubHeader from "../../../components/SubHeader";
import { getLastInformation } from "./shared/getLastInformation";
import { useAuthCheck } from "./shared/useAuthCheck";
import { getInformationList } from "@/api/information";
import { useProjectStore } from "@/stores/project";
import { useInformationStore, InformationListHeader } from "@/stores/information";
import {SortIndicator, SortParams} from "@/components/SortIndicator";
import qs from 'qs';

const getStatusLabel = (startDate: string, endDate: string) => {
  const today = moment();
  if (today.isBefore(startDate, "day")) return "待機中";
  if (today.isAfter(endDate, "day")) return "非公開";
  return "公開中";
};

const isPastDate = (date: string) => {
  return moment().isAfter(date, "day");
};

export interface InformationList {
  information_list: Information[];
}

export type SortPostParams = {
  sort_column_name: string | null,
  sort_order: number | null,
}

const InformationList: FC = () => {
  const navigate = useNavigate();
  const setLoading = useLoadingStore((state) => state.setLoading);
  const [
    selectedProject,
    selectedAdminCategory,
  ] = useProjectStore(state => [
    state.project,
    state.adminCategory,
  ]);
  const [informationQueryParams, setInformationQueryParams, setDoSort, sortParams, setSortParams] = useInformationStore(state => [
    state.queryParams,
    state.setQueryParams,
    state.setDoSort,
    state.sortParams,
    state.setSortParams
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [information, setInformation] = useState<Information[]>([]);

  useAuthCheck();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const breadCrumbs = [
    { title: selectedProject?.name },
    { title: selectedAdminCategory?.name },
    { title: "お知らせ一覧" },
  ];

  const onClickNew = () => {
    navigate("/admin/information/new");
  };

  function getData(params: SortPostParams) {
    setLoading(true);
    const postParams: { [key: string]: SortPostParams; } = {
      'params': params,
    };

    const response = getInformationList(postParams);
    response.then(result => {
      setInformation(result.data.result.information_list);
      setSearchParams(qs.stringify(params));
      setInformationQueryParams(params);
    }).catch(error => {
      setErrorMessage(MESSAGE_NO_E39);
    }).finally(() => {
      setLoading(false);
    });
  }
  const lastInformation = getLastInformation(information);

  useEffect(() => {
    const sortParam = {
      item_name: null,
      order: null,
    }
    getData({
      ...informationQueryParams,
      sort_column_name: sortParam.item_name,
      sort_order: sortParam.order
    });
  }, [])

  const onSort = useCallback((sortParam: SortParams) => {
    console.log(sortParam);
    if (sortParam.item_name) {
      getData({
        ...informationQueryParams,
        sort_column_name: sortParam.item_name,
        sort_order: sortParam.order
      });
    }
  }, [informationQueryParams]);

  useEffect(() => {
    const params = qs.parse(searchParams.toString(), {ignoreQueryPrefix: true});
  }, [searchParams]);

  useEffect(() => {
    if (informationQueryParams) {
      getData(informationQueryParams);
    }
  }, []);

  useEffect(() => {
    setDoSort(onSort);
    return () => {
      setDoSort(() => {
      });
    };
  }, [onSort]);

  return (
    <>
      <div className="sub-header">
        <SubHeader breadCrumbs={breadCrumbs} title="お知らせ管理" />
        <div className="uk-text-right uk-margin-small-bottom">
          最終更新ユーザ: {lastInformation?.modified_by} 最終更新:{" "}
          {moment(lastInformation?.modified).format("YYYY-MM-DD HH:mm")}
        </div>
        <div className="uk-flex-right uk-flex uk-flex-middle">
          <button
            className="uk-button--m uk-button-refer uk-margin-left"
            onClick={onClickNew}
          >
            新規作成
          </button>
        </div>
      </div>
      <div className="container">
        <div className="user-tbl-wrap">
          <table className="user-tbl">
            <thead>
              <tr>
                <th className="uk-text-center">
                  <div className="border-box" />
                  No.
                </th>
                {InformationListHeader.map((column, index) =>
                  <th key={`list-header-${index}`} className="sort-indicator-wrap">
                    <div className="border-box"/>
                    {column.headerName}
                    {column.sortItemName != '' &&
                        <SortIndicator item_name={column.sortItemName} isInformation={true}/>
                    }
                  </th>
                )}
                <th className="uk-text-center">
                  <div className="border-box" />
                  複製
                </th>
              </tr>
            </thead>
            <tbody>
              {information && information.map(
                  (item, index) => (
                    <tr
                      key={item.id}
                      style={{
                        height: "50px",
                        backgroundColor: isPastDate(item.end_date)
                          ? "darkgray"
                          : "",
                      }}
                    >
                      <td className="uk-text-center">
                        <div className="border-box" />
                        {index + 1}
                      </td>
                      <td className="uk-text-center">
                        <div className="border-box" />
                        {getStatusLabel(item.start_date, item.end_date)}
                      </td>
                      <td className="uk-text-center">
                        <div className="border-box" />
                        {item.display_drmdx && (
                          <p style={{ margin: 0 }}>DRM-DX</p>
                        )}
                        {item.display_mypage && (
                          <p style={{ margin: 0 }}>マイページ</p>
                        )}
                      </td>
                      <td className="uk-text-center">
                        <div className="border-box" />
                        {moment(item.start_date).format("YYYY/M/D")}~
                        {moment(item.end_date).format("YYYY/M/D")}
                      </td>
                      <td>
                        <div className="border-box" />
                        <Link to={`/admin/information/${item.id}/edit`}>
                          {item.title}
                        </Link>
                      </td>
                      <td style={{ maxWidth: "500px" }}>
                        <div className="border-box" />
                        <span className="line-clamp-1">{item.details}</span>
                      </td>
                      <td>
                        <div className="border-box" />
                        {item.modified_by}
                      </td>
                      <td className="uk-text-center">
                        <div className="border-box" />
                        {moment(item.modified).format("YYYY/MM/DD HH:mm")}
                      </td>
                      <td className="uk-text-center">
                        <div className="border-box" />
                        <Link to={`/admin/information/${item.id}/duplicate`}>
                          複製
                        </Link>
                      </td>
                    </tr>
                  )
                )}
            </tbody>
          </table>
        </div>
      </div>
      {errorMessage && (
        <ConfirmModal
          text={errorMessage}
          confirmButtonText="OK"
          isShow={!!errorMessage}
          onConfirm={() => {
            setErrorMessage(null);
          }}
        />
      )}
    </>
  );
};

export default InformationList;
